export const CARD_REVIEW_KEY = 'orbcon.card-review.v3';

export const FAMILIES = ['programs', 'hubs', 'resources', 'events', 'courses'] as const;

export type FamilyId = (typeof FAMILIES)[number];

export const FAMILY_LABEL: Record<FamilyId, string> = {
  programs: 'Programs',
  hubs: 'Hubs',
  resources: 'Resources',
  events: 'Events',
  courses: 'Courses',
};

export type ReviewOption = { id: string; name: string; sentence: string };

const CURRENT: ReviewOption = {
  id: 'current',
  name: 'Current',
  sentence: 'What this place shows today.',
};

export const EXAMPLES: readonly { id: string; family: FamilyId; label: string }[] = [
  { id: 'programs-index', family: 'programs', label: 'Programs index and search' },
  { id: 'programs-search', family: 'programs', label: 'Header search' },
  { id: 'programs-bloody-easy', family: 'programs', label: 'Programs index, Bloody Easy' },
  { id: 'hubs-index', family: 'hubs', label: 'Programs index and search' },
  { id: 'hubs-program', family: 'hubs', label: 'Search, program hub' },
  { id: 'hubs-practice', family: 'hubs', label: 'Practice home' },
  { id: 'hubs-home', family: 'hubs', label: 'Public home' },
  { id: 'hubs-library', family: 'hubs', label: 'Library strip' },
  { id: 'hubs-search', family: 'hubs', label: 'Header search' },
  { id: 'resources-list', family: 'resources', label: 'Library list' },
  { id: 'resources-grid', family: 'resources', label: 'Library grid' },
  { id: 'resources-search', family: 'resources', label: 'Search results' },
  { id: 'resources-scan', family: 'resources', label: 'Program scan' },
  { id: 'resources-related', family: 'resources', label: 'Related resources' },
  { id: 'resources-home', family: 'resources', label: 'Public home' },
  { id: 'resources-practice', family: 'resources', label: 'Practice home' },
  { id: 'resources-header', family: 'resources', label: 'Header search' },
  { id: 'events-upcoming', family: 'events', label: 'Events list, upcoming' },
  { id: 'events-past', family: 'events', label: 'Events list, past' },
  { id: 'events-home', family: 'events', label: 'Public home' },
  { id: 'events-practice', family: 'events', label: 'Practice home' },
  { id: 'courses-grid', family: 'courses', label: 'Course catalog, grid' },
  { id: 'courses-list', family: 'courses', label: 'Course catalog, list' },
  { id: 'courses-open', family: 'courses', label: 'Sample, not enrolled' },
  { id: 'courses-progress', family: 'courses', label: 'Sample, in progress' },
  { id: 'courses-practice', family: 'courses', label: 'Practice home' },
  { id: 'courses-sidebar', family: 'courses', label: 'My ORBCoN' },
];

const EXAMPLE_OPTIONS: Record<string, readonly ReviewOption[]> = {
  'programs-index': [
    { id: 'object-room-doors', name: 'Room doors', sentence: 'Stack icon and every hub name. This is a room and its doors.' },
    { id: 'place-open-doors', name: 'Open the doors', sentence: 'The title and every hub name. No icon. The doors are the place.' },
    { id: 'handoff-stack-doors', name: 'Stack of doors', sentence: 'Stack icon, hub count, and every hub name as a door.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'A room. The program name, then each hub listed inside it.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'Program, the name, the full introduction, then each hub as a link. More than four hubs can expand. Enter is plain text. Save is a placeholder.' },
  ],
  'programs-search': [
    { id: 'object-room-count', name: 'Hub count', sentence: 'Stack icon, title, and how many hubs are inside.' },
    { id: 'place-program-first', name: 'Program, then count', sentence: 'The word Program, then the title and the hub count.' },
    { id: 'handoff-hub-tally', name: 'Hub tally row', sentence: 'Stack icon and title, ending in the hub count.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The same room as a search row. The hub count sits at the end in blue.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'Program, the title, how many hubs are inside. Save sits above Enter. Enter is plain text, and its extra hit area is below the word.' },
  ],
  'programs-bloody-easy': [
    { id: 'object-room-more', name: 'More hubs', sentence: 'Stack icon, three hub names, then a line for the rest.' },
    { id: 'place-rest-inside', name: 'Rest stays inside', sentence: 'Three hub names, then a line that the rest stay on the program.' },
    { id: 'handoff-shut-stack', name: 'Shut stack', sentence: 'Three hub names, then shut doors and the full hub count.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'The same room. Three hubs are listed, and the rest stay as a count.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'The full introduction, four hubs as links, then + N more to show the rest. Enter is plain text. Save is a placeholder.' },
  ],
  'hubs-index': [
    { id: 'object-folder-counts', name: 'Folder counts', sentence: 'Folder icon, title, how full it is, and save.' },
    { id: 'place-full-folder', name: 'Fullness, no room', sentence: 'Title and how full the folder is. No icon. Save.' },
    { id: 'handoff-folder-mark', name: 'Folder and mark', sentence: 'Folder icon, title, resource count, and save.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'A folder. A tab shows the resource count, then the hub name and save.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'A column card. Hub, the name, the full description, how many resources it holds, and Open folder. Save sits with the title.' },
  ],
  'hubs-program': [
    { id: 'object-folder-room', name: 'Folder in room', sentence: 'Folder icon, the program name, the hub, its counts, and save.' },
    { id: 'place-named-room', name: 'Room, then fullness', sentence: 'The program name, then the hub and how full it is.' },
    { id: 'handoff-room-mark', name: 'Room and mark', sentence: 'The program in blue, the hub, a resource count, and save.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'A folder. The tab names the program and the resource count.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'A column card. The hub name, then In {program} underneath, and the full description. Save sits with the title.' },
  ],
  'hubs-practice': [
    { id: 'object-folder-tile', name: 'Folder tile', sentence: 'Folder, title, how full it is, and save.' },
    { id: 'place-your-hub', name: 'Your hub row', sentence: 'A practice row: title, how full it is, and save.' },
    { id: 'handoff-folder-tile', name: 'Open folder', sentence: 'A tile that is only the folder and the title.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The same folder as a practice row. The count sits in the blue side.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'A folder row: the count, the name, and save in the corner.' },
  ],
  'hubs-home': [
    { id: 'object-folder-home', name: 'Home folder', sentence: 'Folder icon, title, how full it is, and save.' },
    { id: 'place-hub-invite', name: 'Invite by fullness', sentence: 'A larger title and how full the folder is.' },
    { id: 'handoff-center-folder', name: 'Centered folder', sentence: 'A centered folder, the title, and the resource count.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'The same folder tab on the public home.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'The same hub on the public home, with the full description.' },
  ],
  'hubs-library': [
    { id: 'object-folder-chip', name: 'Room chip', sentence: 'Folder, hub, program name, resource count, and save.' },
    { id: 'place-room-chip', name: 'Room on the chip', sentence: 'Program name and hub title on one chip, and save.' },
    { id: 'handoff-room-chip', name: 'Labeled chip', sentence: 'Folder, the program in gray, and the hub title.' },
    { id: 'solid-chip', name: 'Type rail', sentence: 'The same folder as a chip, with the count in the blue side.' },
    { id: 'modern-chip', name: 'Modern UX', sentence: 'An inline chip, not a full-width row. The hub name is row size, then a gap, then In {program}.' },
  ],
  'hubs-search': [
    { id: 'object-folder-row', name: 'Folder row', sentence: 'Folder, title, program when it has one, resource count, and save.' },
    { id: 'place-hub-first', name: 'Hub, then fullness', sentence: 'The word Hub, the title, and the resource count.' },
    { id: 'handoff-folder-count', name: 'Folder count row', sentence: 'Folder, title, and the resource count.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The same folder in a search row.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'A header panel at least 28rem. The side cap stays 5.5rem. The full hub name, then In {program} under it.' },
  ],
  'resources-list': [
    { id: 'object-doc-chips', name: 'File chips', sentence: 'Document icon, title, date, Under revision, and the live file chips.' },
    { id: 'place-shelf-line', name: 'Shelf status line', sentence: 'Title, then date, Under revision, and the file count. No icon.' },
    { id: 'handoff-page-mark', name: 'Page and mark', sentence: 'Document icon, title, date, Under revision, file count, and save.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'A page. The title and save sit on the sheet. Date, Under revision, and the file count sit in a gray footer.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'A document icon beside Resource, with save beside that word so the title uses the full width. A placeholder byline, and the sentence that names the standard and who it covers. An author-only excerpt has no description. Under revision is gray beside an amber mark, then the date, and PDF or a file count. Open names the document and comes before Save.' },
  ],
  'resources-grid': [
    { id: 'object-doc-column', name: 'Document column', sentence: 'Document icon, title, date, Under revision, and the live file chips.' },
    { id: 'place-file-face', name: 'Files on the face', sentence: 'Title, date, Under revision, and the live file chips. No icon.' },
    { id: 'handoff-sheet-chips', name: 'Sheet and chips', sentence: 'Title, date, Under revision, and the live file chips.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'The same page in the library grid.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'The same document in the library grid, with that clinical sentence.' },
  ],
  'resources-search': [
    { id: 'object-doc-facts', name: 'Document facts', sentence: 'Document icon, title, date, Under revision, file count, and save.' },
    { id: 'place-found-state', name: 'Found document state', sentence: 'Title, date, Under revision, and the file count. No icon.' },
    { id: 'handoff-fact-line', name: 'Fact line', sentence: 'Title, then date, Under revision, and the file count, and save.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'The same page on search results.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'The same document on search results, with that clinical sentence.' },
  ],
  'resources-scan': [
    { id: 'object-doc-scan', name: 'Scan count', sentence: 'Document icon, title, date, Under revision, and the file count.' },
    { id: 'place-scan-ledger', name: 'Ledger on the right', sentence: 'Title on the left. Date, Under revision, and the file count on the right.' },
    { id: 'handoff-scan-count', name: 'Revision check', sentence: 'Title, Under revision, and the file count, and save.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'The same page on a program scan.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'The same document on a program scan, with that clinical sentence.' },
  ],
  'resources-related': [
    { id: 'object-doc-date', name: 'Dated document', sentence: 'Document icon, title, date, Under revision, and the file count.' },
    { id: 'place-neighbor-date', name: 'Neighbor freshness', sentence: 'Title, date, and Under revision.' },
    { id: 'handoff-related-status', name: 'Related status', sentence: 'Title, date, Under revision, and the file count.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'The same page beside a related resource.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'The same document beside a related resource, with that clinical sentence.' },
  ],
  'resources-home': [
    { id: 'object-doc-home', name: 'Home document', sentence: 'Document icon, title, date, Under revision, and the file count.' },
    { id: 'place-doc-invite', name: 'Invite by date', sentence: 'A larger title, the date, and Under revision.' },
    { id: 'handoff-home-status', name: 'Home status', sentence: 'Title, date, Under revision, and the file count.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'The same page on the public home.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'The same document on the public home, with that clinical sentence.' },
  ],
  'resources-practice': [
    { id: 'object-doc-row', name: 'Revision row', sentence: 'Document icon, title, date, Under revision, and the file count.' },
    { id: 'place-your-doc', name: 'Your document row', sentence: 'Title, with the date and Under revision at the end.' },
    { id: 'handoff-revision-row', name: 'Revision and files', sentence: 'Title, Under revision, and the file count.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The same page as a practice row.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'A document row: save beside Resource, the full title, Under revision in gray beside an amber mark, the date, and PDF or a file count.' },
  ],
  'resources-header': [
    { id: 'object-doc-search', name: 'Search document', sentence: 'Document icon, title, date, Under revision, file count, and save.' },
    { id: 'place-resource-first', name: 'Resource, then state', sentence: 'The word Resource, the title, the date, and Under revision.' },
    { id: 'handoff-revision-hit', name: 'Revision hit', sentence: 'Document icon, title, Under revision, and the file count.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The same page in header search.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'The same document row in header search. Save sits beside Resource, and the title uses the full width.' },
  ],
  'events-upcoming': [
    { id: 'object-day-next', name: 'Upcoming day', sentence: 'The day badge, the time, the venue, and Add to Calendar.' },
    { id: 'place-day-venue', name: 'Day beside venue', sentence: 'The day beside the title, then the time, the venue, and Add to Calendar.' },
    { id: 'handoff-day-calendar', name: 'Day and calendar', sentence: 'The day, the title, the time and venue, and a labeled Add to Calendar.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'A date. The day leads, then the title, the time, and Add to Calendar.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'The day beside the title, without repeating the badge date. The title is not cut at two lines. Add to Calendar sits on the first title line. The topic is clamped, the presenter is its own line, and View event sits at the bottom right.' },
  ],
  'events-past': [
    { id: 'object-day-past', name: 'Past day', sentence: 'A gray day, the venue, Watch recording, and Add to Calendar.' },
    { id: 'place-played-day', name: 'Gray day, recording', sentence: 'A gray day, the written date, the venue, and the recording.' },
    { id: 'handoff-watch-day', name: 'Watch the day', sentence: 'A gray day, Watch recording, and Add to Calendar.' },
    { id: 'solid-past', name: 'Type rail', sentence: 'A past date in gray, with Watch recording and Add to Calendar.' },
    { id: 'modern-past', name: 'Modern UX', sentence: 'A gray day, with the year under it, and a title that does not repeat the badge date. Add to Calendar sits on the first title line. The topic is clamped, the presenter is its own line, and Watch recording sits at the bottom right.' },
  ],
  'events-home': [
    { id: 'object-day-home', name: 'Day above', sentence: 'The day above the title, then the time and the venue.' },
    { id: 'place-day-invite', name: 'Day above the venue', sentence: 'The day above a larger title, then the venue.' },
    { id: 'handoff-day-plate', name: 'Day plate', sentence: 'The day above the title, then the venue.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'The day sits above the title on the public home.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'A smaller day beside the title, without repeating the badge date. The topic is clamped, the presenter is its own line, then the time and View event. No Add to Calendar.' },
  ],
  'events-practice': [
    { id: 'object-day-row', name: 'Day row', sentence: 'The day numeral, the title, and the venue.' },
    { id: 'place-your-event', name: 'Your when and where', sentence: 'The title, with the short date and the venue at the end.' },
    { id: 'handoff-date-lead', name: 'Date leads', sentence: 'The date in blue, then the title.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The day sits beside the title in a practice row.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'The day badge aligns to the top of the title, which does not repeat the badge date. One line is the topic, not a second presenter line, then the time. No Add to Calendar.' },
  ],
  'courses-grid': [
    { id: 'object-class-grid', name: 'Class card', sentence: 'A class mark, Completed, the title, and the length.' },
    { id: 'place-completed-face', name: 'Completed on the face', sentence: 'Completed, then the title.' },
    { id: 'handoff-yours-grid', name: 'Yours on the card', sentence: 'Completed, the title, and the length.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'A class. The status band leads. Completed turns that band crimson.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'Course, the name, and a description that drops a repeated title and a trailing eLearning Module. Completed in sage, the length, a full meter, and Review course. A partial meter uses the same figure as its words. The action names the course.' },
  ],
  'courses-open': [
    { id: 'object-class-grid', name: 'Class card', sentence: 'A sample course. A class mark, the title, and the length. Not enrolled.' },
    { id: 'place-completed-face', name: 'Completed on the face', sentence: 'The title and the length. This sample has no enrollment.' },
    { id: 'handoff-yours-grid', name: 'Yours on the card', sentence: 'The title and the length. Enrolling stays on the course page.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'A sample course. Not enrolled, then Start course.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'A sample course you have not joined. Course, the name, the description, Not enrolled, the length, and View course 16px under the length. No meter. The action names the course.' },
  ],
  'courses-progress': [
    { id: 'object-class-grid', name: 'Class card', sentence: 'A sample course. A class mark, In progress, the title, and the length.' },
    { id: 'place-completed-face', name: 'Completed on the face', sentence: 'In progress, then the title.' },
    { id: 'handoff-yours-grid', name: 'Yours on the card', sentence: 'In progress, the title, and the length.' },
    { id: 'solid-column', name: 'Type rail', sentence: 'A sample course in progress, with one module finished, and Continue.' },
    { id: 'modern-column', name: 'Modern UX', sentence: 'A sample course in progress. The length says one of three modules, the meter is part full, and Continue sits 16px under the meter, at the right. The action names the course.' },
  ],
  'courses-list': [
    { id: 'object-class-list', name: 'Class row', sentence: 'A class mark, the title, the length, the modules, and Completed.' },
    { id: 'place-state-at-end', name: 'State at the end', sentence: 'The title, with Completed at the end.' },
    { id: 'handoff-yours-list', name: 'Yours at the end', sentence: 'The title, the length, and Completed at the end.' },
    { id: 'solid-card', name: 'Type rail', sentence: 'The same class as a catalog row, with the status at the start.' },
    { id: 'modern-card', name: 'Modern UX', sentence: 'The same course as a full catalog row. A repeated title and a trailing eLearning Module are dropped. Review course sits at the bottom right and names the course.' },
  ],
  'courses-practice': [
    { id: 'object-class-step', name: 'Your class', sentence: 'A class mark, the title, Completed, and View course.' },
    { id: 'place-your-course', name: 'Your course row', sentence: 'The title, Completed, and View course.' },
    { id: 'handoff-view-row', name: 'View course row', sentence: 'The title, Completed, and View course.' },
    { id: 'solid-row', name: 'Type rail', sentence: 'The same class as a practice row, with View course.' },
    { id: 'modern-row', name: 'Modern UX', sentence: 'A course row: the status, the name, one line of the description with a repeated title and a trailing eLearning Module dropped, and the length. No button.' },
  ],
  'courses-sidebar': [
    { id: 'object-class-side', name: 'Sidebar class', sentence: 'A class mark, the title, Completed, and View course.' },
    { id: 'place-sidebar-step', name: 'Yours, then the step', sentence: 'The full title, Completed, and View course.' },
    { id: 'handoff-view-narrow', name: 'View in the rail', sentence: 'The full title, Completed, and View course.' },
    { id: 'solid-side', name: 'Type rail', sentence: 'The same class in the narrow sidebar, with View course.' },
    { id: 'modern-side', name: 'Modern UX', sentence: 'The same course in the sidebar. A repeated title and a trailing eLearning Module are dropped. Review course sits at the bottom right and names the course.' },
  ],
};

const EXAMPLE_IDS = new Set(EXAMPLES.map((example) => example.id));

export type CardReviewStore = {
  choices: Record<string, string>;
  notes: Record<string, string>;
};

export function exampleOptions(exampleId: string): ReviewOption[] {
  return [CURRENT, ...(EXAMPLE_OPTIONS[exampleId] ?? [])];
}

export function choiceName(exampleId: string, choice: string): string {
  return exampleOptions(exampleId).find((option) => option.id === choice)?.name ?? choice;
}

export function choiceSentence(exampleId: string, choice: string): string {
  return exampleOptions(exampleId).find((option) => option.id === choice)?.sentence ?? '';
}

function allowedChoice(exampleId: string, value: string) {
  return exampleOptions(exampleId).some((option) => option.id === value);
}

export function emptyStore(): CardReviewStore {
  return {
    choices: {},
    notes: {},
  };
}

export function loadStore(): CardReviewStore {
  if (typeof window === 'undefined') return emptyStore();
  try {
    const raw = window.localStorage.getItem(CARD_REVIEW_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as Partial<CardReviewStore>;
    const choices: Record<string, string> = {};
    if (parsed.choices && typeof parsed.choices === 'object') {
      Object.entries(parsed.choices).forEach(([id, value]) => {
        if (EXAMPLE_IDS.has(id) && typeof value === 'string' && allowedChoice(id, value)) choices[id] = value;
      });
    }
    const notes: Record<string, string> = {};
    if (parsed.notes && typeof parsed.notes === 'object') {
      Object.entries(parsed.notes).forEach(([id, note]) => {
        if (EXAMPLE_IDS.has(id) && typeof note === 'string') notes[id] = note;
      });
    }
    return { choices, notes };
  } catch {
    return emptyStore();
  }
}

export function saveStore(store: CardReviewStore) {
  try {
    window.localStorage.setItem(CARD_REVIEW_KEY, JSON.stringify(store));
  } catch {
    // The board still works for this visit if storage is full or blocked.
  }
}

export function withNote(store: CardReviewStore, exampleId: string, note: string): CardReviewStore {
  if (!EXAMPLE_IDS.has(exampleId)) return store;
  return { ...store, notes: { ...store.notes, [exampleId]: note } };
}

export function withChoice(store: CardReviewStore, exampleId: string, optionId: string): CardReviewStore {
  if (!EXAMPLE_IDS.has(exampleId) || !allowedChoice(exampleId, optionId)) return store;
  const next = store.choices[exampleId] === optionId ? '' : optionId;
  return { ...store, choices: { ...store.choices, [exampleId]: next } };
}
